import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function CafePhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <DaylightSpread />
      <Categories />
      <WhatsIncluded />
      <MelbourneContext />
      <PlannerBlock
        eyebrow="Plan it now · Interactive estimator"
        headline="Cost your full café shoot"
        italic="in under a minute."
        blurb="Slide for menu size, tick brunch boards, coffee and venue, see your estimate. Most café shoots come in between $450 and $900 — confirmed on the call."
      />
      <MelbourneSection
        serviceLabel="Cafe Photography"
        eyebrow="Melbourne café scene · Mobile coverage"
        headline="A café photographer for"
        italic="Melbourne mornings."
        intro="Café photography across Sydney Road in Brunswick, Smith Street in Collingwood, Lygon Street in Carlton, High Street in Northcote, Chapel Street in Windsor, Acland Street in St Kilda and Bay Street in Brighton. Daily inner-north and bayside coverage — we arrive before doors and shoot through the brunch service window."
      />
      <SisterServices exclude="cafe" keys={["menu", "food", "venue", "gbp"]} />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on café"
        italic="and brunch photography."
        theme="ink"
        cols={3}
        items={blogsForService("cafe-photography")}
      />
      <FAQSection
        eyebrow="Common questions · Café photography Melbourne"
        headline="Light, timing,"
        italic="and brunch service."
        intro="Most café shoots happen before doors or in a quiet mid-morning window. Bring the espresso machine warm and your best barista on shift — that's all the prep we need."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to refresh your"
        italic="café image library?"
        intro="Quick form below or call directly — quotes confirmed inside one business day. We can walk through your space first if it helps."
        backdropSlug="hero-spread-01"
        formTitle="Tell us about your café shoot."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-spread-01" alt="Cafe photography Melbourne — brunch spread" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Cafe Photography Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Cafe Photographer Melbourne · Brunch · Coffee · Specialty
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          Cafés shot in
          <br />
          their <span className="italic font-light text-ember">best light.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          A specialist café photographer for Melbourne — brunch boards, latte
          art, pastries, brunch service, fit-out and street-front. Shot in
          daylight, in your room, ready for Google, Instagram and your
          website.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Café Shoots From $450
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          Short morning shoot covering brunch hero plates, coffee detail, and
          a sweep of the venue. Full-menu refreshes from $540.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Café Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan The Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Session", "2–4 hours"],
          ["Turnaround", "5–7 days"],
          ["Coverage", "All Melbourne"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why café photography is its own discipline
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Cafés aren't restaurants.
            <br />
            <span className="italic font-light text-ember">Don't shoot them the same.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                A café lives or dies on <strong className="text-bone">morning energy</strong> — bright daylight,
                steam off coffee, hands wrapped around a cup, a busy counter,
                a brunch plate that looks like the table just laughed. That's
                a different brief from a dim restaurant shoot, and it
                rewards a different photographer.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We shoot cafés in their light — natural where possible, top-up
                lighting where the room needs it, never the harsh studio look
                that flattens latte art and bleaches a brunch plate. The room
                should feel like the room your guests post about.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Most café shoots cover all three priorities in one session:
                brunch hero plates, coffee &amp; barista detail, and a quick
                venue sweep — for Instagram, Google Business and your
                website.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Brunch &amp; breakfast plates",
                  "Coffee, latte art &amp; pour shots",
                  "Pastries &amp; bakery counter",
                  "Counter &amp; barista action",
                  "Fit-out &amp; street-front",
                  "Brand details &amp; signage",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b.replace(/&amp;/g, "&")}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="menu-podhi-idli" alt="Cafe photography Melbourne — brunch hero" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="hero-cheese-naan" alt="Cafe coffee photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="menu-falooda" alt="Cafe beverage photography Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const DaylightSpread = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Editorial · The brunch board
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            One board.
            <br />
            <span className="italic font-light text-ember">A week of posts.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A single well-shot brunch board carries the Saturday Instagram,
            the new-menu cover, the EDM banner and the next Meta ad. We
            compose it for every crop you'll need — close, wide, vertical,
            square — in the same frame.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-5">
        <div className="col-span-12 md:col-span-7">
          <Photo slug="hero-spread-01" alt="Cafe brunch board photography Melbourne" ratio="16/10" sizes="(min-width: 768px) 55vw, 100vw" />
        </div>
        <div className="col-span-12 md:col-span-5 grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-5">
          <Photo slug="hero-gulab-jamun-00" alt="Cafe pastry photography Melbourne" ratio="1/1" sizes="20vw" />
          <Photo slug="menu-falooda" alt="Cafe coffee photography Melbourne" ratio="1/1" sizes="20vw" />
        </div>
      </div>
    </div>
  </section>
);

const CATEGORIES = [
  { n: "01", title: "Brunch boards", slug: "hero-spread-01", desc: "Full brunch plates, mixed boards, sharing tables — the photographs Instagram and Google reward the most." },
  { n: "02", title: "Coffee & latte art", slug: "menu-falooda", desc: "Latte art top-down and side, pour shots, espresso pulls, takeaway cups. The shot every café needs and most do badly." },
  { n: "03", title: "Pastries & bakery", slug: "hero-gulab-jamun-00", desc: "Croissants, danishes, scrolls, slices — the counter spread that decides walk-in conversion at 10am." },
  { n: "04", title: "Counter action", slug: "venue-buffet-01", desc: "Barista at the machine, plating at the pass, hands on the counter — café service moments that read as the room actually feels." },
  { n: "05", title: "Fit-out & interior", slug: "hero-banquet-01", desc: "Café interior shot in working daylight — tables, counter, signage, tile, joinery, the line out the door." },
  { n: "06", title: "Street-front", slug: "hero-banquet-01", desc: "The exterior shot Google Business needs — laneway, signage, A-frame, customers spilling onto the footpath." },
  { n: "07", title: "All-day menu", slug: "menu-podhi-idli", desc: "Lunch boards, sandwiches, salads, toasts — the menu items that keep the café busy after the brunch rush." },
  { n: "08", title: "Brand details", slug: "hero-cheese-naan", desc: "Takeaway cups, branded napkins, menu cards, A-frame chalkboards. The micro-imagery social actually engages with." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Café & coffee
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Everything from the
            <br />
            <span className="italic font-light text-ember">counter to the table.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            One morning, one library — coffee, brunch, counter, fit-out and
            street-front. Files ready for Google Business, Instagram, your
            website and any future menu refresh.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Cafe photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const FAQS = [
  { q: "When is the best time to shoot a café in Melbourne?", a: "Almost always 7–10am before the brunch rush, or after 2pm when the room quietens down. North-facing windows on Brunswick's Sydney Road and Collingwood's Smith Street give the best soft daylight — we tend to start at 7:30am to catch the espresso machine warm and avoid queue overspill. Winter shoots run 8:30–11am to dodge the low light angle." },
  { q: "Do we need to close the café?", a: "Almost never. We shoot around service — usually a quiet pre-service window (7:30–9am) for hero plates and latte art, then candid counter and venue shots between 10:30 and 11:30 if you'd like real customers in frame. We work around your two-group espresso queue, not against it." },
  { q: "Can you shoot the coffee machine and barista in action?", a: "Yes — pour shots on a La Marzocco or Synesso, latte art top-down and from the side, espresso pulls with crema detail, hands on the portafilter, the whole barista vocabulary. We bring continuous LED lighting that doesn't blow out crema or flatten a rosetta. Bring your A-shift barista and dial in the grind 30 minutes before." },
  { q: "How many photos do we get from a 2–4 hour shoot?", a: "Typically 25–50 finished frames covering brunch plates (8–12), coffee & latte (6–10), counter / pastry case (6–10), fit-out / street-front (5–8) plus a handful of detail shots. Delivered in a single shareable folder — square, landscape and 9:16 crops included." },
  { q: "How much does café photography cost in Melbourne?", a: "Short morning shoots from $450 covering brunch hero plates (4–6 dishes), coffee detail and a venue sweep. Full menu refreshes from $540 (8–12 dishes). Larger venues with extensive all-day menus from $900–$1,200. All quoted on the call — no travel fees inside Greater Melbourne." },
  { q: "Will the photos work for Google Business Profile and Instagram?", a: "Yes. Every café shoot includes 1:1 square crops, 4:5 vertical for the feed, 9:16 vertical for Stories and Reels, and files sized for Google Business cover (1080×608) and gallery uploads. One shoot, every channel — no recropping by you." },
];

const INCLUSIONS = [
  {
    n: "01",
    t: "Brunch hero plates",
    d: "4–12 finished frames depending on package. Each dish shot in 3 crops — 1:1 square for Instagram and Uber Eats, 4:5 vertical for feed, 16:9 landscape for the website and Google. One re-edit per dish included.",
  },
  {
    n: "02",
    t: "Coffee & latte art",
    d: "6–10 finished frames covering espresso pulls, top-down rosetta and tulip art, side-angle pour shots, takeaway cup hero, and a milk pour sequence. Lit with continuous LED — crema preserved, no flat flash look.",
  },
  {
    n: "03",
    t: "Pastry & counter case",
    d: "Croissant, danish, scroll and slice detail — both single-item hero and full counter spread. Cabinet glass cleaned and styled before frames captured. Useful for 10am Google posts and morning Stories.",
  },
  {
    n: "04",
    t: "Venue interior & street-front",
    d: "5–8 frames covering the room in working daylight, the espresso machine and counter line, the A-frame, signage and laneway exterior — sized for Google Business cover (1080×608) and gallery uploads.",
  },
  {
    n: "05",
    t: "Reels-ready vertical clips",
    d: "Optional add-on (from $180) — 3–5 short 9:16 clips for Instagram Reels and Stories. Latte pour, plate placement, pastry close-up. Music-cleared, ready to post.",
  },
  {
    n: "06",
    t: "Delivery folder, all channels",
    d: "Single Dropbox or Google Drive link, organised by channel (web / Google / Instagram / Uber Eats). Web-optimised JPGs plus print-grade originals. Full commercial licence for your own and earned channels.",
  },
];

const WhatsIncluded = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Inclusions · What you receive
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            What's included in
            <br />
            <span className="italic font-light text-ember">a café shoot.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            Every café package below ships in 5–7 days, includes 1 free re-edit
            per dish, and is licensed for your own and earned channels (your
            website, Google Business, Instagram, Uber Eats, EDM, in-store
            screens). No surprise fees, no per-channel re-licensing.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        {INCLUSIONS.map((i) => (
          <div key={i.n} className="bg-bone p-7 md:p-8">
            <span className="font-display italic text-2xl text-ember leading-none">{i.n}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-3">{i.t}</h3>
            <p className="mt-4 text-sm md:text-base text-char-mute leading-relaxed">{i.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MELB_NOTES = [
  {
    n: "Brunswick & Carlton",
    d: "Sydney Road, Lygon Street and the Brunswick East strip. Most venues we shoot here are 18–35 seat specialty rooms with a 2-group espresso machine and a brunch-led menu. Daylight comes in long and low through east-facing windows — best between 7:30am and 10am.",
  },
  {
    n: "Collingwood, Fitzroy & Northcote",
    d: "Smith Street, Gertrude Street, Brunswick Street and High Street. Tighter inner-north laneway frontages, often warehouse-style fit-outs with steel and timber. Reels and venue exteriors work hardest here — laneway shots double as Google Business covers.",
  },
  {
    n: "South Melbourne, Albert Park & Port",
    d: "Clarendon Street, Bridport Street, Bay Street. Bayside light is harder and whiter — we'll add a soft scrim on the south-facing windows. Larger floor plans, family brunch volume from 10:30am, so we wrap hero plates before 10am.",
  },
  {
    n: "St Kilda, Elwood & Brighton",
    d: "Acland Street, Ormond Road, Bay Street Brighton. Mixed brunch + bakery + bar venues. Travel from CBD is 25–35 minutes — we schedule first slot of the morning to dodge Beach Road traffic, especially weekends.",
  },
];

const MelbourneContext = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Melbourne café context · By suburb
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Different streets,
            <br />
            <span className="italic font-light text-ember">different light.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed">
            The brief doesn't change much — coffee, brunch, venue, street-front.
            What changes is the light, the room size, the traffic and the
            customer flow. Quick notes from shoots across the city below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-bone/10 border border-bone/10">
        {MELB_NOTES.map((m) => (
          <div key={m.n} className="bg-ink p-7 md:p-8">
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight text-ember">{m.n}</h3>
            <p className="mt-3 text-sm md:text-base text-bone/80 leading-relaxed">{m.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
